import type { CustomCellRendererProps } from "ag-grid-react";
import { COLOR } from "application/src/settings/colors-settings.ts";
import { nanoid } from "nanoid";
import { forwardRef, useRef } from "react";
import { useNavigate } from "react-router";
import {
  ActionButton,
  ActionButtonIntent,
  ActionButtonSize,
  DialogComponent,
  Icons,
  ItemTile,
  Popover,
  SymbolIcon,
} from "widgets";
import { useOverlay } from "widgets/hooks";
import { getCssColor } from "widgets/utils";

import type { Sample } from "../../api/graphql.ts";
import { URL_ENTITY_TYPES } from "../../settings/entity-types.ts";
import { TableCellWrapper } from "./TableCellWrapper.tsx";

export const SampleCell = (props: CustomCellRendererProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay({});

  if (!props.value) {
    return <TableCellWrapper style={{ textAlign: "right" }} {...props} />;
  }

  const sample = extractSampleValues(props.value);

  return (
    <TableCellWrapper tooltipValue={sample.name ?? "[Restricted Sample]"} style={{ textAlign: "left" }} {...props}>
      {sample.hidden ? (
        <ActionButton
          icon={"security"}
          label={"Restricted Sample"}
          size={ActionButtonSize.InTableCell}
          intent={ActionButtonIntent.Outlined}
          aria-label={"Restricted Sample"}
        />
      ) : (
        <>
          <ActionButton
            {...triggerProps}
            ref={triggerRef}
            icon={<img src={Icons.Action.Sample} width={12} height={12} />}
            size={ActionButtonSize.InTableCell}
            intent={ActionButtonIntent.Outlined}
            aria-label={"Show more details"}
            label={sample.name ?? ""}
          />
          <Popover state={state} triggerRef={triggerRef} {...overlayProps}>
            <DialogComponent closeFn={state.close} width={400}>
              <SampleTile sample={sample} />
            </DialogComponent>
          </Popover>
        </>
      )}
    </TableCellWrapper>
  );
};

interface SampleTileProps {
  sample: Sample;
}

const SampleTile = forwardRef<HTMLDivElement, SampleTileProps>(function SampleTile(props, ref) {
  const sample: Sample = props.sample;

  const navigate = useNavigate();

  return (
    <ItemTile
      hint={
        sample.deleted ? <SymbolIcon icon={"delete"} style={{ color: COLOR.DARKISH_PINK.BORDER }} size={16} /> : null
      }
      id={sample.id}
      name={sample.name ?? ""}
      description={sample.description}
      onPress={() => {
        if (!sample.deleted) {
          navigate(`/${URL_ENTITY_TYPES.SAMPLE}/${sample.id}`);
        }
      }}
      icon={<img src={Icons.Tiles.Sample} width={24} height={30} />}
      typeLabel={sample.type?.name}
      dateInfoTag={sample.modified ?? sample.created ?? undefined}
      project={sample.project}
      infoTags={[
        {
          id: nanoid(), // just a key for React. Maybe consider the ID to be sent from the server??
          color: sample.status?.color ? getCssColor(sample.status?.color) : undefined,
          children: sample.status?.name,
        },
      ]}
      ref={ref}
      private={sample.locked ?? false}
    />
  );
});

// FIXME: We already discussed that we need types from the server.
// eslint-disable-next-line
function extractSampleValues(value: any): Sample {
  return {
    id: value.id,
    name: value.name,
    description: value.description,
    locked: value.locked,
    hidden: value.hidden,
    deleted: value.deleted,
    created: value.created,
    modified: value.modified,
    status: value.statusName
      ? ({
          name: value.statusName,
          color: value.statusColor,
        } as Sample["status"])
      : undefined,
    project:
      value.projectId > 0
        ? ({
            id: value.projectId,
            name: value.projectName,
            color: value.projectColor,
          } as Sample["project"])
        : undefined,
    type: {
      id: value.typeId,
      name: value.typeName,
    } as Sample["type"],
  } as Sample;
}
